import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, mergeProps as _mergeProps, renderList as _renderList, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "price-icon-group" }
const _hoisted_2 = {
  key: 0,
  class: "price"
}
const _hoisted_3 = {
  key: 1,
  class: "pricing-icon"
}
const _hoisted_4 = {
  key: 0,
  class: "lines-icon"
}
const _hoisted_5 = {
  key: 2,
  class: "lines-icon"
}
const _hoisted_6 = {
  key: 3,
  class: "mdi mdi-tag-text-outline"
}
const _hoisted_7 = {
  key: 4,
  class: "mdi mdi-calculator"
}
const _hoisted_8 = {
  key: 5,
  class: "mdi mdi-cash-multiple"
}
const _hoisted_9 = { class: "price-formula" }
const _hoisted_10 = { class: "pricing-popover" }
const _hoisted_11 = { class: "popover-header" }
const _hoisted_12 = { class: "reference-main" }
const _hoisted_13 = { class: "part-number" }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = {
  key: 0,
  class: "reference-secondary"
}
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { key: 2 }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { key: 1 }
const _hoisted_21 = { key: 2 }
const _hoisted_22 = { key: 3 }

import { computed } from 'vue';
import { convertLength } from '@/utils/priceListUtils';
import type {
  PriceSource,
  PriceType,
  PricingPricelist,
  PricingReference,
} from '@/types/pricing';

type IconType = 'interpolation' | 'exact' | 'reference-formula' | 'pricelist' | 'formula' | 'cost' | 'none';


export default /*@__PURE__*/_defineComponent({
  __name: 'LinePricingInfo',
  props: {
    price: {},
    priceType: {},
    priceSource: { default: null },
    references: {},
    pricelist: {},
    showPrice: { type: Boolean, default: true },
    clickable: { type: Boolean, default: false },
    basePrice: { default: null },
    perMeterAdder: { default: null },
    lengthUnit: { default: null }
  },
  setup(__props: any) {

const props = __props;

const hasReferences = computed(() => (props.references?.length ?? 0) > 0);

// Pointer cursor when there's an action: either the consumer marked the whole
// element clickable, or the tooltip exposes a clickable sale-order link.
// Otherwise it stays default (the tooltip still opens on hover).
const hasAction = computed(
  () => props.clickable || (props.references ?? []).some((ref) => !!ref.saleOrderId),
);

// Icon encodes the pricing method. Shared methods (series/exact/reference_formula)
// use the same icon for engine and manual — provenance is conveyed by color.
// The backend derives priceType reference-aware (references win over a base +
// adder), so a manual 'formula' always means a hand-entered one.
const iconType = computed<IconType>(() => {
  switch (props.priceType) {
    case 'series':
      return 'interpolation';
    case 'exact':
      return 'exact';
    case 'reference_formula':
      return 'reference-formula';
    case 'price_list':
      return 'pricelist';
    case 'formula':
      return 'formula';
    case 'cost':
      return 'cost';
    // 'static' (flat manual price) and unpriced/unknown render with no icon.
    default:
      return 'none';
  }
});

// Color encodes provenance: manual = orange, engine = blue (cost keeps teal).
const colorClass = computed(() => {
  if (props.priceSource === 'manual') return 'manual';
  switch (props.priceType) {
    case 'cost':
      return 'cost';
    case 'price_list':
    case 'series':
    case 'exact':
    case 'reference_formula':
      return 'engine';
    default:
      return 'none';
  }
});

// Header shown above the reference list. Manual: the references were chosen by
// the user (not necessarily "closest" / an "exact match"), so the wording just
// counts them, whatever the stored method. Engine: the method picks the wording.
const referenceHeader = computed(() => {
  if (props.priceSource === 'manual') {
    return (props.references?.length ?? 0) >= 2
      ? 'Priced manually based on two references:'
      : 'Priced manually based on one reference:';
  }
  switch (props.priceType) {
    case 'exact':
      return 'Based on exact match:';
    case 'series':
      return 'Based on two closest references:';
    case 'reference_formula':
      return 'Based on a reference with base and adder:';
    default:
      return 'Based on:';
  }
});

const hasTooltip = computed(() => {
  if (hasReferences.value) return true;
  if (props.priceType === 'price_list') return !!props.pricelist;
  if (props.priceType === 'cost') return true;
  if (props.priceSource === 'manual') return true;
  return false;
});

const formatPrice = (value: number): string => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Subtle "$base + $adder/{unit}" breakdown shown after the icon. Only when a
// flat base and a non-zero per-meter adder are both present. The stored adder
// is per meter; convert to per foot when the line's unit is feet. A negative
// (signed) adder renders as "− $x" instead of "+ $-x".
const formulaBreakdown = computed<string | null>(() => {
  const base = props.basePrice;
  const perMeterAdder = props.perMeterAdder;
  if (base === null || base === undefined) return null;
  if (perMeterAdder === null || perMeterAdder === undefined || perMeterAdder === 0) return null;

  const isFoot = (props.lengthUnit ?? '').toUpperCase() === 'F';
  const adderInUnit = isFoot ? convertLength(perMeterAdder, 'F', false, true) : perMeterAdder;
  const unitLabel = isFoot ? 'F' : 'M';
  const sign = adderInUnit < 0 ? '\u2212' : '+';
  return `${formatPrice(base)} ${sign} ${formatPrice(Math.abs(adderInUnit))}/${unitLabel}`;
});

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatOrderState = (state: string): string => {
  switch (state.toLowerCase()) {
    case 'placed': return 'Order Placed';
    case 'sale': return 'Order Placed';
    case 'partial': return 'Partial';
    case 'quoted': return 'Quote';
    case 'draft': return 'Draft';
    case 'sent': return 'Quote Sent';
    case 'done': return 'Done';
    case 'cancel':
    case 'cancelled': return 'Cancelled';
    default: return state;
  }
};

// Combine the reference's order date and state into a single trailing
// parenthetical, e.g. "(April 14, 2026, Done)". Either part may be missing.
const formatRefMeta = (ref: PricingReference): string => {
  const parts: string[] = [];
  if (ref.orderDate) parts.push(formatDate(ref.orderDate));
  if (ref.orderState) parts.push(formatOrderState(ref.orderState));
  // Leading space is part of the value (template whitespace gets stripped by Vue).
  return parts.length ? ` (${parts.join(', ')})` : '';
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    location: "end top",
    offset: 4,
    "open-on-hover": "",
    "open-delay": 200,
    "close-delay": 300,
    disabled: !hasTooltip.value
  }, {
    activator: _withCtx(({ props }) => [
      _createElementVNode("span", _mergeProps(props, {
        class: ["line-pricing-info", [colorClass.value, { interactive: hasAction.value, 'has-formula': !!formulaBreakdown.value }]]
      }), [
        _createElementVNode("span", _hoisted_1, [
          (_ctx.showPrice && _ctx.price !== null)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(formatPrice(_ctx.price)), 1))
            : _createCommentVNode("", true),
          (iconType.value !== 'none')
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
                (iconType.value === 'interpolation')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, _cache[0] || (_cache[0] = [
                      _createElementVNode("i", { class: "mdi mdi-text" }, null, -1),
                      _createElementVNode("i", { class: "mdi mdi-check lines-badge" }, null, -1)
                    ])))
                  : (iconType.value === 'exact')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode("=")
                      ], 64))
                    : (iconType.value === 'reference-formula')
                      ? (_openBlock(), _createElementBlock("span", _hoisted_5, _cache[1] || (_cache[1] = [
                          _createElementVNode("i", { class: "mdi mdi-text" }, null, -1),
                          _createElementVNode("i", { class: "mdi mdi-function-variant lines-badge" }, null, -1)
                        ])))
                      : (iconType.value === 'pricelist')
                        ? (_openBlock(), _createElementBlock("i", _hoisted_6))
                        : (iconType.value === 'formula')
                          ? (_openBlock(), _createElementBlock("i", _hoisted_7))
                          : (iconType.value === 'cost')
                            ? (_openBlock(), _createElementBlock("i", _hoisted_8))
                            : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        (formulaBreakdown.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _cache[2] || (_cache[2] = _createElementVNode("i", { class: "mdi mdi-arrow-right formula-arrow" }, null, -1)),
              _createElementVNode("span", _hoisted_9, _toDisplayString(formulaBreakdown.value), 1)
            ], 64))
          : _createCommentVNode("", true)
      ], 16)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_10, [
        (hasReferences.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_11, _toDisplayString(referenceHeader.value), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.references, (ref, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "reference-item"
                }, [
                  _createElementVNode("div", _hoisted_12, [
                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "bullet" }, "·", -1)),
                    _createElementVNode("span", _hoisted_13, _toDisplayString(ref.partNumber), 1),
                    (ref.saleOrderName)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _cache[3] || (_cache[3] = _createTextVNode(" in ")),
                          (ref.saleOrderId)
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                to: `/home/sale-orders/${ref.saleOrderId}`,
                                class: "sale-order-link"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(ref.saleOrderName), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(ref.saleOrderName), 1))
                        ], 64))
                      : _createCommentVNode("", true),
                    (formatRefMeta(ref))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(formatRefMeta(ref)), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (ref.customerName)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(ref.customerName), 1))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ], 64))
          : (_ctx.priceType === 'price_list' && _ctx.pricelist)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "popover-header" }, "Based on pricelist:", -1)),
                _createElementVNode("div", null, [
                  _createTextVNode(_toDisplayString(_ctx.pricelist.name), 1),
                  (_ctx.pricelist.effectiveDate)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, " (Effective: " + _toDisplayString(formatDate(_ctx.pricelist.effectiveDate)) + ")", 1))
                    : _createCommentVNode("", true)
                ])
              ], 64))
            : (_ctx.priceType === 'cost')
              ? (_openBlock(), _createElementBlock("div", _hoisted_18, "Calculated from cost"))
              : (_ctx.priceSource === 'manual')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                    (_ctx.priceType === 'formula' && formulaBreakdown.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_19, "Priced manually based on " + _toDisplayString(formulaBreakdown.value) + " formula.", 1))
                      : (_ctx.priceType === 'formula')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_20, "Priced manually based on a base and adder formula."))
                        : (_ctx.price !== null)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, "Priced manually at " + _toDisplayString(formatPrice(_ctx.price)) + ".", 1))
                          : (_openBlock(), _createElementBlock("div", _hoisted_22, "Priced manually."))
                  ], 64))
                : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["disabled"]))
}
}

})